import React from 'react';
import PropTypes from 'prop-types';
import { Alert, Col, Row } from 'reactstrap';
import ResourceProperty from '../../../common/resource/ResourceProperty';
import EnumStatus from '../../../common/EnumStatus';
import Badge from '../../../common/Badge';
import {
  DEPOSIT_MIGRATION_FILE_STATUS,
  DEPOSIT_MIGRATION_MOVEMENT_CLASSIFICATION,
} from '../../../../config/constants';
import { DATE_FORMAT, FULL_DATE_TIME_FORMAT } from '../../../../config/locale';
import { tzNormalizeDate } from '../../../../utils/date';

const formatDate = (date, format) =>
  date ? tzNormalizeDate({ date, format }) : '-';

const DepositMigrationBasicInformationResource = ({
  id,
  status,
  errorMessage,
  bankName,
  statementAccountNumber,
  fileName,
  fileUrl,
  statementFrom,
  statementTo,
  totalMovements,
  processedCount,
  partialMatchCount,
  unmatchedCount,
  discardedCount,
  migratedCount,
  failedCount,
  operatorUserName,
  createDate,
}) => (
  <>
    {errorMessage ? (
      <Alert color="danger" className="small">
        {errorMessage}
      </Alert>
    ) : null}

    <Row>
      <Col md={6}>
        <h3>Información Básica</h3>
        <ResourceProperty label="ID:">{id}</ResourceProperty>
        <ResourceProperty label="Estado:">
          <EnumStatus
            enumObject={DEPOSIT_MIGRATION_FILE_STATUS}
            enumValue={status}
          />
        </ResourceProperty>
        <ResourceProperty label="Entidad bancaria:">
          {bankName || '-'}
        </ResourceProperty>
        <ResourceProperty label="Archivo:">
          {fileUrl ? (
            <a href={fileUrl} target="_blank" rel="noopener noreferrer">
              {fileName}
            </a>
          ) : (
            fileName || '-'
          )}
        </ResourceProperty>

        <h3 className="mt-3">Datos detectados en el archivo</h3>
        <ResourceProperty label="Cuenta:">
          {statementAccountNumber || '-'}
        </ResourceProperty>
        <ResourceProperty label="Rango de fechas:">
          {`${formatDate(statementFrom, DATE_FORMAT)} - ${formatDate(
            statementTo,
            DATE_FORMAT,
          )}`}
        </ResourceProperty>

        <h3 className="mt-3">Información interna</h3>
        <ResourceProperty label="Fecha de carga:">
          {formatDate(createDate, FULL_DATE_TIME_FORMAT)}
        </ResourceProperty>
        <ResourceProperty label="Creado por:">
          {operatorUserName || '-'}
        </ResourceProperty>
      </Col>

      <Col md={6}>
        <h3>Resultado del cruce</h3>
        <ResourceProperty label="Movimientos leídos:">
          {totalMovements}
        </ResourceProperty>
        <ResourceProperty
          label={`${DEPOSIT_MIGRATION_MOVEMENT_CLASSIFICATION.PROCESSED.label}:`}
        >
          <Badge color="success" text={processedCount} />
        </ResourceProperty>
        <ResourceProperty
          label={`${DEPOSIT_MIGRATION_MOVEMENT_CLASSIFICATION.PARTIAL_MATCH.label}:`}
        >
          <Badge
            color={partialMatchCount > 0 ? 'warning' : 'light'}
            text={partialMatchCount}
          />
        </ResourceProperty>
        <ResourceProperty
          label={`${DEPOSIT_MIGRATION_MOVEMENT_CLASSIFICATION.UNMATCHED.label}:`}
        >
          <Badge
            color={unmatchedCount > 0 ? 'danger' : 'light'}
            text={unmatchedCount}
          />
        </ResourceProperty>
        <ResourceProperty
          label={`${DEPOSIT_MIGRATION_MOVEMENT_CLASSIFICATION.DISCARDED.label}:`}
        >
          <Badge color="secondary" text={discardedCount} />
        </ResourceProperty>

        <h3 className="mt-3">Migración a SAP</h3>
        <ResourceProperty label="Migrados:">
          <Badge color="success" text={migratedCount} />
        </ResourceProperty>
        <ResourceProperty label="Fallidos:">
          <Badge
            color={failedCount > 0 ? 'danger' : 'light'}
            text={failedCount}
          />
        </ResourceProperty>
      </Col>
    </Row>
  </>
);

DepositMigrationBasicInformationResource.propTypes = {
  id: PropTypes.number.isRequired,
  status: PropTypes.string.isRequired,
  errorMessage: PropTypes.string,
  bankName: PropTypes.string,
  statementAccountNumber: PropTypes.string,
  fileName: PropTypes.string,
  fileUrl: PropTypes.string,
  statementFrom: PropTypes.string,
  statementTo: PropTypes.string,
  totalMovements: PropTypes.number,
  processedCount: PropTypes.number,
  partialMatchCount: PropTypes.number,
  unmatchedCount: PropTypes.number,
  discardedCount: PropTypes.number,
  migratedCount: PropTypes.number,
  failedCount: PropTypes.number,
  operatorUserName: PropTypes.string,
  createDate: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

DepositMigrationBasicInformationResource.defaultProps = {
  errorMessage: null,
  bankName: null,
  statementAccountNumber: null,
  fileName: null,
  fileUrl: null,
  statementFrom: null,
  statementTo: null,
  totalMovements: 0,
  processedCount: 0,
  partialMatchCount: 0,
  unmatchedCount: 0,
  discardedCount: 0,
  migratedCount: 0,
  failedCount: 0,
  operatorUserName: null,
  createDate: null,
};

export default DepositMigrationBasicInformationResource;
